import { useEffect } from "react";
import styled from "styled-components";

// Transient bottom toast, pinned above the TabBar. Auto-dismisses after
// `duration` ms; render conditionally (parent owns the message state).
const Wrap = styled.div`
  position: fixed;
  left: 50%;
  bottom: calc(72px + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  z-index: 100;
  width: calc(100% - ${({ theme }) => theme.space(8)});
  max-width: ${({ theme }) => theme.maxWidth};
  pointer-events: none;
`;

const Bubble = styled.div`
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space(3)} ${theme.space(4)}`};
  background: ${({ theme }) => theme.color.surfaceRaised};
  border: 1px solid ${({ theme }) => theme.color.borderHi};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: 0 12px 32px rgba(2, 4, 8, 0.5);
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.color.text};
  text-align: center;
  animation: toast-rise 0.18s ease-out;

  @keyframes toast-rise {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export function Toast({
  message,
  onDismiss,
  duration = 3500,
}: {
  message: string;
  onDismiss: () => void;
  duration?: number;
}) {
  useEffect(() => {
    const id = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(id);
  }, [message, duration, onDismiss]);

  return (
    <Wrap role="status" aria-live="polite">
      <Bubble>{message}</Bubble>
    </Wrap>
  );
}
